import { Badge } from "@/components/ui/badge";
import { Search, Target, Rocket, BarChart3 } from "lucide-react";

const B2BMethodology = () => {
  const steps = [
    {
      number: "01",
      icon: Search,
      title: "Discovery & Analysis",
      description: "Deep-dive into your ICP, competitive landscape, and current marketing performance to identify opportunities.",
      duration: "Week 1-2",
      deliverables: ["Market analysis", "ICP refinement", "Gap assessment"]
    },
    {
      number: "02",
      icon: Target,
      title: "Strategy & Planning",
      description: "Custom growth roadmap with channel strategy, messaging framework, and campaign architecture.",
      duration: "Week 2-3",
      deliverables: ["Growth roadmap", "Channel strategy", "KPI framework"]
    },
    {
      number: "03",
      icon: Rocket,
      title: "Launch & Execution",
      description: "Campaign activation across selected channels with continuous A/B testing and optimization.",
      duration: "Week 3-4",
      deliverables: ["Campaign launch", "Content creation", "Tech setup"]
    },
    {
      number: "04",
      icon: BarChart3,
      title: "Scale & Optimize",
      description: "Performance analysis, winning strategies scaled, new opportunities identified and tested.",
      duration: "Ongoing",
      deliverables: ["Weekly reporting", "Optimization", "Expansion"]
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-secondary/5">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Our Methodology</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              How We Build B2B Growth Engines
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A proven 4-phase approach that delivers predictable, scalable pipeline for B2B companies.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index}
                  className="relative bg-background rounded-xl p-6 border border-border hover:border-accent/50 transition-colors group"
                >
                  {/* Step Number */}
                  <div className="absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br from-secondary to-accent rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-primary">{step.number}</span>
                  </div>

                  {/* Icon */}
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 mt-2 group-hover:bg-secondary/20 transition-colors">
                    <IconComponent className="w-6 h-6 text-secondary" />
                  </div>

                  {/* Content */}
                  <h3 className="text-lg font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{step.description}</p>

                  {/* Duration */}
                  <Badge variant="outline" className="mb-4 text-xs">
                    {step.duration}
                  </Badge>

                  {/* Deliverables */}
                  <div className="space-y-1">
                    {step.deliverables.map((deliverable, i) => (
                      <p key={i} className="text-xs text-muted-foreground flex items-center gap-2">
                        <span className="w-1 h-1 bg-accent rounded-full" />
                        {deliverable}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BMethodology;
